// src/features/executive/sales/widgets/PaymentMethodMixCard.tsx
// Donut + legend showing the share of revenue by payment method — one slice
// per master payment channel (settings → finance), labelled from the master
// row, with vs-previous-period variance. Unknown channels arrive as "other".
import { Box, Chip, Stack, Typography } from '@mui/material'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { Doughnut } from 'react-chartjs-2'
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip as ChartTooltip,
} from 'chart.js'
import WidgetCard from 'features/executive/components/WidgetCard/WidgetCard'
import { usePaymentMethodMix } from '../hooks/use-payment-method-mix'
import { formatCurrency, formatVariance } from 'utils/executive.utils'
import { paymentMethodColor, paymentMethodLabel } from '../payment-method-mix.utils'

ChartJS.register(ArcElement, ChartTooltip, Legend)

// Don't surface the "largest mover" highlight unless the swing is large
// enough to be informative — small ±2–5% shifts add noise.
const MOVER_THRESHOLD = 0.10

function varianceOf(current: number, previous: number | null | undefined): number | null {
  if (previous == null || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

export default function PaymentMethodMixCard() {
  const { t, i18n } = useTranslation()
  const { data, loading, error, refetch } = usePaymentMethodMix()
  const language = i18n.language

  const isEmpty = !data || data.total === 0

  const chartData = useMemo(() => {
    if (!data) return null
    return {
      labels: data.methods.map((m) => paymentMethodLabel(m, language)),
      datasets: [
        {
          data: data.methods.map((m) => m.amount),
          backgroundColor: data.methods.map((m, i) => paymentMethodColor(m, i)),
        },
      ],
    }
  }, [data, language])

  const totalVariance = data ? varianceOf(data.total, data.previousTotal) : null

  // Largest mover: find the method with the highest absolute variance above
  // the threshold. Useful for catching payment-behaviour shifts (e.g. patients
  // suddenly favouring PromptPay over cards).
  const movers = (data?.methods ?? [])
    .map((m) => ({ m, v: varianceOf(m.amount, m.previousAmount) }))
    .filter((x): x is { m: typeof x.m; v: number } => x.v != null && Math.abs(x.v) >= MOVER_THRESHOLD)
    .sort((a, b) => Math.abs(b.v) - Math.abs(a.v))
  const largestMover = movers[0] ?? null

  const notice = largestMover ? (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
      {largestMover && (
        <Chip
          icon={
            largestMover.v >= 0 ? (
              <TrendingUpIcon />
            ) : (
              <TrendingDownIcon />
            )
          }
          label={
            <Typography variant="caption">
              {t(
                largestMover.v >= 0
                  ? 'EXECUTIVE.SALES.PAYMENT_METHOD.LARGEST_MOVER_UP'
                  : 'EXECUTIVE.SALES.PAYMENT_METHOD.LARGEST_MOVER_DOWN',
                largestMover.v >= 0
                  ? 'เพิ่มสูงสุด: {{label}} {{variance}}'
                  : 'ลดลงมากสุด: {{label}} {{variance}}',
                {
                  label: paymentMethodLabel(largestMover.m, language),
                  variance: formatVariance(largestMover.v),
                }
              )}
            </Typography>
          }
          color={largestMover.v >= 0 ? 'success' : 'error'}
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
    </Stack>
  ) : null

  return (
    <WidgetCard
      title={t('EXECUTIVE.SALES.PAYMENT_METHOD.TITLE', 'ยอดขายตามวิธีชำระเงิน')}
      titleTooltip={t(
        'EXECUTIVE.SALES.PAYMENT_METHOD.TOOLTIP',
        'สัดส่วนยอดที่ลูกค้าชำระจริง แยกตามช่องทางที่ตั้งค่าไว้ใน ตั้งค่า → จัดการเงิน — นับตามวันที่ลูกค้าชำระ ไม่ใช่วันเปิดบิล\nไม่รวม Gift Voucher และโปรโมชัน (เป็นส่วนลด) · ช่องทางที่ไม่พบในระบบแสดงเป็น "อื่นๆ"'
      )}
      notice={notice}
      loading={loading}
      error={error}
      empty={isEmpty}
      emptyMessage={t(
        'EXECUTIVE.SALES.PAYMENT_METHOD.EMPTY',
        'ไม่พบการชำระเงินในช่วงเวลาและสาขาที่เลือก'
      )}
      onRetry={refetch}
      skeletonHeight={380}
    >
      {data && (
        <>
          <Box sx={{ height: 200, position: 'relative', mb: 2 }}>
            <Doughnut
              data={chartData as any}
              options={{
                maintainAspectRatio: false,
                responsive: true,
                cutout: '70%',
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (ctx) =>
                        `${ctx.label}: ${formatCurrency(Number(ctx.parsed))} (${(
                          (Number(ctx.parsed) / data.total) *
                          100
                        ).toFixed(1)}%)`,
                    },
                  },
                },
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {t('EXECUTIVE.SALES.PAYMENT_METHOD.TOTAL', 'รวม')}
              </Typography>
              <Typography variant="h6">{formatCurrency(data.total)}</Typography>
              {totalVariance != null && (
                <Typography
                  variant="caption"
                  color={totalVariance >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatVariance(totalVariance)}
                </Typography>
              )}
            </Box>
          </Box>
          <Stack spacing={0.5}>
            {data.methods.map((m, i) => {
              const variance = varianceOf(m.amount, m.previousAmount)
              return (
                <Box
                  key={m.key}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: paymentMethodColor(m, i),
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {paymentMethodLabel(m, language)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(m.amount)} ({m.percent.toFixed(1)}%)
                  </Typography>
                  {variance != null && (
                    <Typography
                      variant="caption"
                      color={variance >= 0 ? 'success.main' : 'error.main'}
                      sx={{ minWidth: 56, textAlign: 'right' }}
                    >
                      {formatVariance(variance)}
                    </Typography>
                  )}
                </Box>
              )
            })}
          </Stack>
        </>
      )}
    </WidgetCard>
  )
}
